// PublishMoreCard — cross-promotion tile on /corridor that surfaces the
// caller's already-located but not-yet-published scans and jumps them
// straight into Gallery select mode (with those scans pre-checked) so
// they can bulk-publish in one flow.
//
// Rendered ONLY when there's something to publish — the corridor page
// stays clean for users who are all caught up.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Sprout, ArrowRight } from "lucide-react";

import { corridor as corridorApi } from "@/lib/corridorApi";

const MAX_PREVIEW_THUMBS = 4;

export default function PublishMoreCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = not yet loaded, [] = none

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await corridorApi.eligible();
        if (!cancelled) setItems(data?.items || []);
      } catch {
        // Never block the corridor page over a discovery card — a silent
        // no-op is fine, the map is what matters.
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hidden state during load and when nothing to publish. Zero DOM cost
  // for the "caught up" case, which is what we want for repeat visitors.
  if (!items || items.length === 0) return null;

  const preview = items.slice(0, MAX_PREVIEW_THUMBS);
  const overflow = Math.max(0, items.length - MAX_PREVIEW_THUMBS);

  const jump = () => {
    // ?preselect=corridor tells Gallery to fetch /corridor/eligible,
    // enter select mode, and preselect matching scan_ids. Gallery then
    // strips the query param so a shallow reload doesn't re-trigger.
    navigate("/gallery?preselect=corridor");
  };

  return (
    <div
      className="mt-4 rounded-3xl border border-leaf/25 bg-gradient-to-br from-leaf/[0.06] to-transparent p-4"
      data-testid="corridor-publish-more"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-2xl bg-leaf/15 flex items-center justify-center">
          <Sprout className="w-5 h-5 text-leaf" strokeWidth={1.5} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg text-leaf-ink leading-tight">
            {t("corridor.publishMore.title", { count: items.length })}
          </h2>
          <p className="mt-1 text-[13px] text-leaf-mist leading-relaxed">
            {t("corridor.publishMore.desc")}
          </p>
          {preview.length > 0 && (
            <ul
              className="mt-3 flex items-center gap-2"
              aria-label={t("corridor.publishMore.previewAria")}
              data-testid="corridor-publish-more-preview"
            >
              {preview.map((it) => (
                <li key={it.scan_id} className="shrink-0">
                  <div
                    className="w-10 h-10 rounded-xl overflow-hidden bg-parchment ring-1 ring-white/60 shadow-sm"
                    title={it.plant_name || ""}
                    data-testid={`corridor-publish-more-thumb-${it.scan_id}`}
                  >
                    {it.thumb_base64 ? (
                      <img src={it.thumb_base64} alt="" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                </li>
              ))}
              {overflow > 0 && (
                <li className="shrink-0">
                  <div
                    className="w-10 h-10 rounded-xl bg-parchment text-leaf-ink text-[11px] font-medium flex items-center justify-center ring-1 ring-white/60 shadow-sm tabular-nums"
                    aria-label={t("corridor.publishMore.overflowAria", { n: overflow })}
                    data-testid="corridor-publish-more-overflow"
                  >
                    +{overflow}
                  </div>
                </li>
              )}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={jump}
          data-testid="corridor-publish-more-cta"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-leaf text-white text-xs uppercase tracking-[0.14em] font-medium px-4 py-2 hover:bg-leaf-deep transition"
        >
          {t("corridor.publishMore.cta")}
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </div>
  );
}
